import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Button, ChangeList, ExplainerBanner, Sheet, Skeleton, SuccessBlock, Textarea } from '@/ds'
import { ContactRows } from '@/app/components/ContactRows'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { peticionesService } from '@/services/peticiones'
import { reservaService } from '@/services/reserva'
import type { ChangeOptionType, ChangeQuote, Stay } from '@/services/reserva'
import { useApiLang, useSave } from '../Checkin/useSave'
import { BackLink } from '../Pagos/shared'
import { RequestTracking } from './RequestTracking'
import { alternativesText, BOOKING_ROUTES, changeSummary, longRange, readDraft, TYPE_BY_SLUG, useBookingStay, useLang, useChangeExit } from './shared'
import type { UnavailableReason } from './shared'

function banner(reason: UnavailableReason, type: ChangeOptionType, quote: ChangeQuote | null, stay: Stay, lang: string, t: TFunction): { title: string; text: string } {
  switch (reason) {
    case 'busy':
      return quote?.busy ? { title: t('v2.reserva.noDisponible.ocupado', { unit: stay.unit.name, range: longRange(quote.busy.from, quote.busy.to, lang, t) }), text: t('v2.reserva.noDisponible.ocupadoTexto') } : { title: t('v2.reserva.noDisponible.rechazadoTitulo'), text: t('v2.reserva.noDisponible.rechazadoTexto') }
    case 'rejected':
      return { title: t('v2.reserva.noDisponible.rechazadoTitulo'), text: t('v2.reserva.noDisponible.rechazadoTexto') }
    case 'channel':
      return stay.channel.name ? { title: t('v2.reserva.noDisponible.canalTitulo', { channel: stay.channel.name }), text: t('v2.reserva.noDisponible.canalTexto', { channel: stay.channel.name }) } : { title: t('v2.reserva.noDisponible.propiedadTitulo'), text: t('v2.reserva.noDisponible.propiedadTexto') }
    case 'property':
      return type === 'cancel' ? { title: t('v2.reserva.noDisponible.cancelarTitulo'), text: t('v2.reserva.noDisponible.propiedadTexto') } : { title: t('v2.reserva.noDisponible.propiedadTitulo'), text: t('v2.reserva.noDisponible.propiedadTexto') }
  }
}

/** Texto precargado de la petición: lo que el huésped quería hacer. */
function requestText(type: ChangeOptionType, quote: ChangeQuote | null, stay: Stay, lang: string, t: TFunction): string {
  if (quote) return t(`v2.reserva.noDisponible.texto.${type}`, { change: changeSummary(quote, stay, lang, t) })
  return type === 'cancel' ? t('v2.reserva.noDisponible.texto.cancel') : t('v2.reserva.noDisponible.texto.generico', { change: t(`v2.reserva.cambios.${type}`).toLowerCase() })
}

/**
 * 8.5 · Cambio no posible (Reserva.dc.html screen=unavailable, §5g): sin disponibilidad, canal o propiedad que no lo
 * permiten. Aviso (no error), qué fechas están libres si se saben, y pedirlo a
 * la propiedad como petición con seguimiento (mock; peticiones en solo lectura, bloque 6). Sin peticiones en la API
 * actual, el contacto con la propiedad, como ConsultNotAllowedView.tsx.
 */
export function NoDisponible() {
  const { tipo } = useParams()
  const { pathname } = useLocation()
  const type: ChangeOptionType = pathname.startsWith(BOOKING_ROUTES.cancel) ? 'cancel' : (TYPE_BY_SLUG[tipo ?? ''] ?? 'dates')
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useBookingStay()
  const { go, search } = useAppNavigate()
  const back = useChangeExit()
  const [draft] = useState(() => readDraft(stay.guestCode, type))
  const quote = draft?.quote ?? null
  const reason: UnavailableReason = draft?.reason ?? 'property'
  const [options] = useAsync(`reserva-cambios:${stay.guestCode}:peticion`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const o = asyncValue(options)
  const [sheet, setSheet] = useState(false)
  const [text, setText] = useState(() => requestText(type, quote, stay, lang, t))
  const [missing, setMissing] = useState(false)
  const [requestId, setRequestId] = useState<string | null>(null)
  const { saving, error, run } = useSave()
  const [request] = useAsync(`peticion:${stay.guestCode}:${requestId ?? ''}`, (signal) => (requestId ? peticionesService.get(stay, requestId, signal) : Promise.resolve(null)))

  const b = banner(reason, type, quote, stay, lang, t)
  const range = quote && 'checkIn' in quote.input ? longRange(quote.input.checkIn, quote.input.checkOut, lang, t) : null
  const canRetryDates = (type === 'dates' || type === 'nights') && (reason === 'busy' || reason === 'rejected')
  const whatsapp = stay.contact.whatsapp

  const send = async () => {
    if (!text.trim()) return setMissing(true)
    const res = await run(() => reservaService.requestChange(stay, { type, text: text.trim() }))
    if (res) {
      setRequestId(res.requestId)
      setSheet(false)
    }
  }

  const actions = [
    ...(canRetryDates
      ? [{ id: 'otras', title: t('v2.reserva.noDisponible.otras'), condition: (quote && alternativesText(quote, lang, t)) ?? t('v2.reserva.noDisponible.otrasSub'), href: withQuery(BOOKING_ROUTES.pick(type), search), onClick: () => go(BOOKING_ROUTES.pick(type)) }]
      : []),
    ...(o?.canRequest && !requestId ? [{ id: 'pedir', title: t('v2.reserva.noDisponible.pedir'), condition: o.requestResponseHours ? t('v2.reserva.noDisponible.pedirSub', { count: o.requestResponseHours }) : t('v2.reserva.noDisponible.pedirSubSinPlazo'), onClick: () => setSheet(true) }] : []),
  ]

  return (
    <main className="page reserva">
      <div className="pagos__head">
        <BackLink to={back.path} label={back.backLabel} query={{ volver: null }} />
        <h1>{t('v2.reserva.noDisponible.titulo')}</h1>
        {range && <p className="page__lead">{t('v2.reserva.noDisponible.lead', { range: range.charAt(0).toUpperCase() + range.slice(1) })}</p>}
      </div>
      <ExplainerBanner tone="warning" title={b.title}>
        {b.text}
      </ExplainerBanner>

      {requestId && (
        <>
          <SuccessBlock title={t('v2.reserva.noDisponible.enviada')} headingLevel={2}>
            {o?.requestResponseHours ? t('v2.reserva.noDisponible.enviadaTexto', { count: o.requestResponseHours }) : t('v2.reserva.noDisponible.enviadaSinPlazo')}
          </SuccessBlock>
          {request.kind === 'ready' && request.value ? <RequestTracking request={request.value} /> : request.kind === 'loading' ? <Skeleton height={200} /> : null}
        </>
      )}

      <h2 className="pagos__h2">{t('v2.reserva.noDisponible.queHacer')}</h2>
      {options.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={128} />
        </div>
      ) : (
        <>
          {actions.length > 0 && <ChangeList items={actions} />}
          {!o?.canRequest && (
            <div className="pagos__stack">
              <ContactRows whatsapp={whatsapp} phone={stay.contact.supportPhone} reference={stay.reference} message={text} />
              <p className="pagos__note">{t('v2.legacy.preview.labels.modifyBookingView.consultNotAllowed.infoMessage')}</p>
            </div>
          )}
        </>
      )}

      <div className="pagos__group">
        {o?.canRequest && !requestId && (
          <Button onClick={() => setSheet(true)}>{t('v2.reserva.noDisponible.pedir')}</Button>
        )}
        <Button variant="secondary" onClick={() => back.exit()}>
          {back.returnLabel}
        </Button>
      </div>

      <Sheet open={sheet} title={t('v2.reserva.noDisponible.pedir')} confirmLabel={t('v2.reserva.noDisponible.enviar')} onConfirm={() => void send()} onCancel={() => setSheet(false)} loading={saving}>
        <div className="pagos__group">
          <Textarea
            label={t('v2.reserva.noDisponible.campo')}
            value={text}
            rows={4}
            onChange={(e) => {
              setText(e.target.value)
              setMissing(false)
            }}
            error={missing ? t('v2.legacy.validations.Required') : undefined}
          />
          {error && (
            <span className="pagos__error" role="alert">
              {error}
            </span>
          )}
        </div>
      </Sheet>
    </main>
  )
}
